/**
 * The design editor: a full-screen page to change a design's panels, obstacles and roof faces, with live numbers
 * from the quote engine, comments and the version history. Every save is a new version (the API recomputes what
 * follows); opening and editing a design is free.
 *
 * Keyboard: hotkeys.ts holds the map (tools, edits, views; ? shows it and lets the user rebind keys). The
 * handler here ignores keys typed into fields, and Esc unwinds one layer at a time.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Box,
  Circle,
  Hand,
  History,
  Keyboard,
  Layers as LayersIcon,
  Magnet,
  Map as MapIcon,
  MessageSquare,
  MousePointer2,
  Pentagon,
  Plus,
  Presentation,
  Redo2,
  RotateCcw,
  RotateCw,
  Ruler,
  Save,
  Sparkles,
  Spline,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { Alert, Badge, Button, Modal, NumberInput, Select, Spinner, TextInput, useToast } from "../components/ui";
import { carryMessage } from "./carry.ts";
import { useIntlShape, useT, type Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { DirIcon } from "../i18n/mirror";
import { useOrg } from "../api/org";
import { config } from "../config";
import { pickMapsKey, shouldFetchMapsKey } from "../lib/mapsKey.ts";
import { date, money, num, shownVersion } from "../lib/format";
import { BomPanel } from "./BomPanel";
import { useWorkspaceSwitch, WorkspaceSwitch } from "../components/WorkspaceSwitch";
import { useBackGuard } from "./backGuard";
import { workspaceForAction } from "./workspace.ts";
import { PermitSummary } from "../permits/PermitSummary";
import { CheatSheet, Keys } from "./CheatSheet";
import { CommentsPanel, useCommentThreads } from "./Comments";
import type { Vec2, Vec3 } from "./geo.ts";
import { formatCombo, isMac, isTypingTarget, loadOverrides, matchAction, resolveBindings, saveOverrides, type Overrides } from "./hotkeys.ts";
import { faceContext, localConflicts, panelGeometry, CONFLICT_LABEL_KEYS } from "./layout.ts";
import { draftFrom, editBody, editorObstacles, editorPanels, isDirty, newKey, reduce, type Draft, type PanelItem } from "./model.ts";
import { duplicatePanels, faceForSelectAll, nudgeObstacle, nudgePanels, NUDGE_BIG_M, NUDGE_M } from "./ops.ts";
import { perfDebugEnabled } from "./perf.ts";
import { PerfOverlay } from "./PerfOverlay";
import { OptionsPanel } from "./OptionsPanel";
import type { PaymentType } from "./payments.ts";
import { useDesignSetbacks, useLiveSavings, useSavingsInputs } from "./planHooks.ts";
import { PresentationOverlay, type FaceToggle } from "./Presentation";
import { SavingsPanel } from "./SavingsPanel";
import { SetbacksPanel } from "./SetbacksPanel";
import { SideTabs, type SideTab as SideTabDef } from "./SideTabs";
import { liveRoof } from "./roof.ts";
import { bestFill, type ModuleSize, type Orientation } from "./snap.ts";
import { ALL_LAYERS, TopView, type Layers, type Selection, type Tool, type TopViewApi } from "./TopView";
import type { ThreeViewApi } from "./ThreeView";
import type { Box as PlanBox } from "./viewport.ts";
import { useEditorAnalysis } from "./layers/controller";
import { AnalysisDock, AnalysisMenuRows } from "./layers/Dock";
import { ShadeReportPanel } from "./layers/ShadeReport";
import "./layers/layers.css";

const ThreeView = lazy(() => import("./ThreeView"));
const ThreeAnalysis = lazy(() => import("./layers/ThreeAnalysis"));

const OBSTACLE_KINDS: S["ObstructionKind"][] = ["vent", "chimney", "skylight", "hvac", "dish", "hatch", "structure", "unknown"];
const PREVIEW_DEBOUNCE_MS = 600;
const NO_LAYERS: Layers = { imagery: true, setbacks: false, obstacles: false, panels: false, comments: false };
/** Presentation shows the house and its panels only (no rule checks: the customer sees the proposal). */
const NO_CONFLICTS: Record<string, never> = {};
/** Presentation shows the house and its panels only. */
const PRESENT_LAYERS: Layers = { imagery: true, setbacks: false, obstacles: false, panels: true, comments: false };

type SideTab = "panels" | "obstacles" | "roof" | "comments" | "versions" | PlanTab;
/** The plan panels (second row of tabs): selling and ordering, and the shade report, next to the design tabs. */
type PlanTab = "savings" | "options" | "setbacks" | "bom" | "permits" | "shade";
/** [tab, label on the bar, full name for the tooltip and the More menu, the hotkey action that opens it] */
const PLAN_TABS: [PlanTab, MessageKey, MessageKey, string][] = [
  ["savings", "editor.side.plan.savings", "editor.side.plan.savings", "panel.savings"],
  ["options", "editor.side.plan.options", "editor.side.plan.optionsName", "panel.options"],
  ["setbacks", "editor.side.plan.setbacks", "editor.side.plan.setbacks", "panel.setbacks"],
  ["bom", "editor.side.plan.bom", "editor.side.plan.bomName", "panel.bom"],
  ["permits", "editor.side.plan.permits", "editor.side.plan.permitsName", "panel.permits"],
  ["shade", "editor.side.plan.shade", "editor.side.plan.shadeName", "layer.report"],
];
const DESIGN_TABS: Record<Exclude<SideTab, PlanTab>, MessageKey> = {
  panels: "editor.side.tab.panels",
  obstacles: "editor.side.tab.obstacles",
  roof: "editor.side.tab.roof",
  comments: "editor.side.tab.comments",
  versions: "editor.side.tab.versions",
};
type ViewMode = "2d" | "3d" | "present";

/** Each tool's hint for the status line (what to do, and the keys that help). */
const TOOL_HINTS: Record<Tool, MessageKey> = {
  select: "editor.hint.select",
  add: "editor.hint.add",
  polygon: "editor.hint.polygon",
  circle: "editor.hint.circle",
  roof: "editor.hint.roof",
  measure: "editor.hint.measure",
  pan: "editor.hint.pan",
  comment: "editor.hint.comment",
  section: "editor.hint.section",
};

function useImage(url: string | undefined) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!url) return;
    let revoked: string | null = null;
    let cancelled = false;
    rawFetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(blob);
        setObjectUrl(revoked);
      })
      .catch(() => setObjectUrl(null));
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [url]);
  return objectUrl;
}

/** The Google Maps browser key for the 3D view: config.js's, else the API's (GET /api/v1/config/maps), asked once a
 * designer opens the 3D view. While it loads, `loading` is true; a failed request means no key (own imagery). */
function useMapsBrowserKey(wanted: boolean): { key: string; loading: boolean } {
  const fetchKey = shouldFetchMapsKey(config.mapsBrowserKey, wanted);
  const fromApi = useQuery({
    queryKey: ["config", "maps"],
    queryFn: () => unwrap(api.GET("/v1/config/maps")),
    enabled: fetchKey,
    staleTime: Infinity,
    retry: 1,
  });
  return { key: pickMapsKey(config.mapsBrowserKey, fromApi.data), loading: fetchKey && fromApi.isLoading };
}

export function DesignEditor() {
  const { designId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const [version, setVersion] = useState<number | null>(null);
  useEffect(() => setVersion(null), [designId]); // another option of the customer opened
  const doc = useQuery({
    queryKey: ["editor", designId, version],
    queryFn: () =>
      unwrap(
        api.GET("/v1/designs/{design_id}/editor", {
          params: { path: { design_id: designId }, query: version ? { version } : {} },
        }),
      ),
  });
  if (doc.isLoading) return <Spinner label={t("editor.open.loading")} />;
  if (doc.error || !doc.data)
    return (
      <div className="editor-error">
        <Alert tone="danger" title={t("editor.open.failed")}>
          {errorMessage(doc.error)}
        </Alert>
        <Button onClick={() => navigate(`/designs/${designId}`)}>{t("editor.action.back")}</Button>
      </div>
    );
  if (doc.data.mode === "sketch" && !doc.data.roof_model) return <Navigate to={`/designs/${designId}/sketch`} replace />;
  if (!doc.data.roof_model || !doc.data.module)
    return (
      <div className="editor-error">
        <Alert tone="warn" title={t("editor.open.nothingTitle")}>
          {t("editor.open.nothingBody")}
        </Alert>
        <Button onClick={() => navigate(`/designs/${designId}`)}>{t("editor.action.back")}</Button>
      </div>
    );
  return (
    <EditorWorkspace
      key={`${doc.data.design_id}:${doc.data.version_number}`}
      doc={doc.data}
      onOpenVersion={(n) => setVersion(n)}
      onSaved={(n) => {
        setVersion(null);
        qc.invalidateQueries({ queryKey: ["editor", designId] });
        qc.invalidateQueries({ queryKey: ["versions", designId] });
        qc.invalidateQueries({ queryKey: ["design", designId] });
        for (const key of ["design-setbacks", "design-options", "bom"]) qc.invalidateQueries({ queryKey: [key] });
        toast(t("editor.save.saved", { version: n }));
      }}
    />
  );
}

function EditorWorkspace({
  doc,
  onSaved,
  onOpenVersion,
}: {
  doc: S["EditorDocument"];
  onSaved: (number: number) => void;
  onOpenVersion: (number: number | null) => void;
}) {
  const designId = doc.design_id;
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const navigate = useNavigate();
  const module = doc.module as ModuleSize;
  const [history, dispatch] = useReducer(reduce, { past: [], present: draftFrom(doc), pending: null, future: [] });
  const draft = history.present;
  const [params, setParams] = useSearchParams();
  const linkedComment = params.get("comment");
  const [view, setView] = useState<ViewMode>("2d");
  const maps = useMapsBrowserKey(view !== "2d");
  const [tool, setToolState] = useState<Tool>("select");
  const [tab, setTab] = useState<SideTab>(linkedComment ? "comments" : "panels");
  const [selection, setSelection] = useState<Selection>(null);
  const [placement, setPlacement] = useState<"sticky" | "free">("sticky");
  const [orientation, setOrientation] = useState<Orientation>((doc.panels[0]?.orientation as Orientation) ?? "portrait");
  const [obstacleKind, setObstacleKind] = useState<S["ObstructionKind"]>("vent");
  const [layers, setLayers] = useState<Layers>(ALL_LAYERS);
  const [hiddenLayers, setHiddenLayers] = useState<Layers | null>(null);
  const [layersOpen, setLayersOpen] = useState(false);
  const [note, setNote] = useState("");
  const [drawing, setDrawing] = useState(0);
  const [panHeld, setPanHeld] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [overrides, setOverridesState] = useState<Overrides>(() => loadOverrides());
  const [pendingComment, setPendingComment] = useState<PlanBox | null>(null);
  const [activeComment, setActiveComment] = useState<string | null>(linkedComment);
  const [showResolved, setShowResolved] = useState(false);
  // "Design with AI" from a Sketch: whether its drawn panels were carried over (editor/carry.ts)
  const carry = useMemo(() => (doc.is_current ? carryMessage(doc.layout_carry) : null), [doc.is_current, doc.layout_carry]);
  const [carryClosed, setCarryClosed] = useState(false);
  const [perf] = useState(() => perfDebugEnabled());
  const finishRef = useRef<(() => void) | null>(null);
  const topApi = useRef<TopViewApi>(null);
  const threeApi = useRef<ThreeViewApi>(null);
  const mac = useMemo(() => isMac(), []);
  const bindings = useMemo(() => resolveBindings(overrides), [overrides]);
  const keyOf = (id: string) => bindings.get(id)?.[0] ?? null;
  const org = useOrg();
  /** Editing needs designs_leads edit (a Viewer opens the editor read only); older versions are always read only. */
  const canEdit = org.can("designs_leads", "edit");
  const historical = !doc.is_current;
  const readOnly = historical || !canEdit;
  const imageUrl = useImage(doc.image?.url);
  const comments = useCommentThreads(designId);
  const threads = useMemo(() => comments.data?.items ?? [], [comments.data]);
  const [presentView, setPresentView] = useState<"2d" | "3d">("3d");
  const [payment, setPayment] = useState<PaymentType>("cash");
  const savingsInputs = useSavingsInputs(designId, canEdit && !historical);
  const setbacks = useDesignSetbacks(designId, doc.version_number, history.present.obstacles, history.present.dirty.obstacles);
  /** The setbacks the editor draws and snaps to: the rules being previewed, else the version's. */
  const setbacksDoc = setbacks.preview ?? doc.setbacks;
  const planDoc = useMemo(() => (setbacks.preview ? { ...doc, setbacks: setbacks.preview } : doc), [doc, setbacks.preview]);

  const setOverrides = (o: Overrides) => {
    setOverridesState(o);
    saveOverrides(o);
  };
  const setTool = useCallback(
    (next: Tool) => {
      const allowed: Tool[] = readOnly ? ["select", "measure", "pan", "comment", "section"] : [];
      if (readOnly && !allowed.includes(next)) return;
      setToolState(next);
      if (next === "add") setTab("panels");
      if (next === "polygon" || next === "circle") setTab("obstacles");
      if (next === "roof") setTab("roof");
      if (next === "comment") setTab("comments");
    },
    [readOnly],
  );

  const roof = useMemo(() => liveRoof(doc.roof_model!, draft.roof), [doc.roof_model, draft.roof]);
  const conflicts = useMemo(() => localConflicts(draft.panels, roof, draft.obstacles, setbacksDoc, module), [draft.panels, roof, draft.obstacles, setbacksDoc, module]);
  const selectedKeys = useMemo(() => new Set(selection?.kind === "panels" ? selection.keys : []), [selection]);
  const analysis = useEditorAnalysis({ designId, doc, roof, draft, module, reportOpen: tab === "shade" });

  // ------------------------------------------------------------------ live numbers (debounced)
  const [numbers, setNumbers] = useState<S["EditorNumbers"]>(doc.numbers);
  const [pricing, setPricing] = useState(false);
  const [serverConflicts, setServerConflicts] = useState<S["PanelConflict"][]>([]);
  const requestId = useRef(0);
  useEffect(() => {
    if (!draft.dirty.panels && !draft.dirty.obstacles) {
      setNumbers(doc.numbers);
      return;
    }
    const id = ++requestId.current;
    setPricing(true);
    const timer = window.setTimeout(() => {
      unwrap(
        api.POST("/v1/designs/{design_id}/editor/preview", {
          params: { path: { design_id: designId } },
          body: {
            version_number: doc.version_number,
            panels: editorPanels(draft.panels),
            ...(draft.dirty.obstacles ? { obstructions: editorObstacles(draft.obstacles) } : {}),
          },
        }),
      )
        .then((r) => {
          if (id !== requestId.current) return;
          setNumbers(r.numbers);
          setServerConflicts(r.conflicts);
        })
        .catch((e) => id === requestId.current && toast(errorMessage(e), true))
        .finally(() => id === requestId.current && setPricing(false));
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft.panels, draft.obstacles, draft.dirty.panels, draft.dirty.obstacles, designId, doc.version_number, doc.numbers, toast]);

  // ------------------------------------------------------------------------------ actions
  const setPanels = useCallback((panels: PanelItem[], transient?: boolean) => dispatch({ type: "panels", panels, transient }), []);
  const selected = draft.panels.filter((p) => selectedKeys.has(p.key));
  const deleteSelected = () => {
    if (selection?.kind === "panels") {
      setPanels(draft.panels.filter((p) => !selectedKeys.has(p.key)));
      setSelection(null);
    } else if (selection?.kind === "obstacle") {
      dispatch({ type: "obstacles", obstacles: draft.obstacles.filter((o) => o.key !== selection.key) });
      setSelection(null);
    }
  };
  const rotateSelected = (deg: number) => {
    if (!selected.length) return;
    setPanels(draft.panels.map((p) => (selectedKeys.has(p.key) ? { ...p, rotation: ((p.rotation + deg + 540) % 360) - 180 } : p)));
  };
  const flip = (o: Orientation): Orientation => (o === "portrait" ? "landscape" : "portrait");
  const flipSelected = () => {
    if (!selected.length) return;
    setPanels(draft.panels.map((p) => (selectedKeys.has(p.key) ? { ...p, orientation: flip(p.orientation) } : p)));
  };
  const fillSelectedFace = (faceId: string) => {
    const face = roof.faces.find((f) => f.id === faceId);
    if (!face) return;
    const ctx = faceContext(face, roof, draft.panels, draft.obstacles, setbacksDoc, module);
    const placed = draft.panels.filter((p) => p.face_id === face.id).map((p) => ({ center: p.center, orientation: p.orientation, rotationDeg: p.rotation }));
    const { centers, orientation: chosen, grid } = bestFill(ctx, placed, orientation, module, doc.gap_m);
    if (!centers.length) return toast(t("editor.panels.noSpace"), true);
    setPanels([...draft.panels, ...centers.map((c) => ({ key: newKey("p"), id: null, face_id: face.id, orientation: chosen, center: c, rotation: grid.rotationDeg }))]);
    toast(t("editor.panels.added", { count: centers.length }));
  };
  const clearFace = (faceId: string) => setPanels(draft.panels.filter((p) => p.face_id !== faceId));

  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  /** To the Permit workspace (or one of its links): a plan set is made from a saved version, so offer to save. */
  const goPermit = (href = `/designs/${designId}/permit`) => {
    if (dirty && !readOnly) setLeaveTo(href);
    else navigate(href);
  };
  const save = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/designs/{design_id}/edits", { params: { path: { design_id: designId } }, body: editBody(doc.version_number, draft, note) })),
    onSuccess: (r) => {
      // nothing differed from the saved version after all: no new version, start again from it
      if (r.unchanged) {
        dispatch({ type: "reset", draft: draftFrom(doc) });
        return toast(t("editor.save.nothing"));
      }
      onSaved(r.version.number);
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  /** One save at a time: a double click (or Save then Ctrl+S) used to send a second save of the same version, which the
   * API refused as a conflict and the editor showed as an error although the first one had saved (live test, EX-02). */
  const saving = useRef(false);
  const saveOnce = (after?: () => void) => {
    if (saving.current) return;
    saving.current = true;
    save.mutate(undefined, { onSuccess: () => after?.(), onSettled: () => void (saving.current = false) });
  };

  /** Unsaved edits: what differs from the saved version (`withDirty`). Setbacks rules that changed since the version are
   * information on the Setbacks tab with their own Apply, never unsaved changes: opening a design is never dirty. */
  const dirty = isDirty(draft);
  /** The Setbacks tab's Apply: a new version with the rules the design uses today (`apply_setbacks`). */
  const applySetbacks = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/designs/{design_id}/edits", { params: { path: { design_id: designId } }, body: { base_version: doc.version_number, note: null, apply_setbacks: true } })),
    onSuccess: (r) => {
      if (r.unchanged) {
        void setbacks.refetch();
        return toast(t("editor.save.nothing"));
      }
      onSaved(r.version.number);
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  // a reload or closing the tab with unsaved edits asks first (the Sketch does the same)
  useEffect(() => {
    if (!dirty || readOnly) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, readOnly]);
  // …and so does the browser's Back (editor/backGuard.ts)
  const [backAsked, setBackAsked] = useState(false);
  const backGuard = useBackGuard(dirty && !readOnly, () => setBackAsked(true));
  // Design | Sketch | Permit: unsaved work is saved first or kept (editor/workspace.ts)
  const workspace = useWorkspaceSwitch({ designId, current: "design", state: { dirty, readOnly }, save: () => save.mutateAsync() });
  const workspaceKeys = {
    design: keyOf("mode.design") && formatCombo(keyOf("mode.design")!, mac),
    sketch: keyOf("mode.sketch") && formatCombo(keyOf("mode.sketch")!, mac),
    permit: keyOf("mode.permit") && formatCombo(keyOf("mode.permit")!, mac),
  };
  const presenting = view === "present";
  const liveSavings = useLiveSavings(designId, doc.version_number, draft.panels, savingsInputs.inputs, savingsInputs.loaded && draft.panels.length > 0 && (tab === "savings" || presenting));

  // Presentation: a face's panels off (kept aside) and on again (restored, or filled when it had none).
  const parked = useRef(new Map<string, PanelItem[]>());
  const faceToggles: FaceToggle[] = roof.faces.map((f) => {
    const count = draft.panels.filter((p) => p.face_id === f.id).length;
    return { id: f.id, label: f.label, panels: count || parked.current.get(f.id)?.length || 0, on: count > 0 };
  });
  const toggleFace = (faceId: string, on: boolean) => {
    if (!on) {
      parked.current.set(faceId, draft.panels.filter((p) => p.face_id === faceId));
      setPanels(draft.panels.filter((p) => p.face_id !== faceId));
      return;
    }
    const back = parked.current.get(faceId);
    parked.current.delete(faceId);
    if (back?.length) setPanels([...draft.panels, ...back]);
    else fillSelectedFace(faceId);
  };
  const faceForSelection = selection?.kind === "face" ? roof.faces.find((f) => f.id === selection.id) : selected[0] ? roof.faces.find((f) => f.id === selected[0]!.face_id) : null;
  const conflictCount = Object.keys(conflicts).length;
  const currency = numbers.currency ?? "USD";

  const openComment = useCallback(
    (id: string | null) => {
      setActiveComment(id);
      if (id) setTab("comments");
      const next = new URLSearchParams(params);
      if (id) next.set("comment", id);
      else next.delete("comment");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // A link from a mention email opens the thread and frames its box once the threads are loaded.
  const framedLink = useRef(false);
  useEffect(() => {
    if (framedLink.current || !linkedComment || !threads.length) return;
    const thread = threads.find((x) => x.id === linkedComment);
    framedLink.current = true;
    if (!thread) return toast(t("editor.comments.gone"), true);
    if (thread.status === "resolved") setShowResolved(true);
    window.setTimeout(() => topApi.current?.frame([thread.anchor.min_xy as Vec2, thread.anchor.max_xy as Vec2]), 50);
  }, [linkedComment, threads, toast, t]);

  /** What F frames: the selection, the open comment, else the house. */
  const frame = () => {
    if (view !== "2d") {
      const xyz: Vec3[] = selected.flatMap((p) => panelGeometry(p, roof, module).xyz);
      threeApi.current?.frame(xyz);
      return;
    }
    let pts: Vec2[] = [];
    if (selection?.kind === "panels") pts = selected.flatMap((p) => panelGeometry(p, roof, module).plan);
    else if (selection?.kind === "obstacle") pts = draft.obstacles.find((o) => o.key === selection.key)?.polygon ?? [];
    else if (selection?.kind === "face") pts = roof.faces.find((f) => f.id === selection.id)?.plan ?? [];
    else if (activeComment) {
      const t = threads.find((x) => x.id === activeComment);
      if (t) pts = [t.anchor.min_xy as Vec2, t.anchor.max_xy as Vec2];
    }
    topApi.current?.frame(pts);
  };

  const nudge = (dx: number, dy: number) => {
    if (readOnly) return;
    if (selection?.kind === "panels" && selected.length) setPanels(nudgePanels(draft.panels, selectedKeys, [dx, dy], roof, module), true);
    else if (selection?.kind === "obstacle") dispatch({ type: "obstacles", obstacles: nudgeObstacle(draft.obstacles, selection.key, [dx, dy]) });
  };

  const duplicate = () => {
    if (readOnly || !selected.length) return;
    const out = duplicatePanels(draft.panels, selectedKeys, roof, draft.obstacles, setbacksDoc, module, doc.gap_m, placement === "sticky");
    if (!out) return toast(t("editor.panels.noRoomForCopy"), true);
    setPanels(out.panels);
    setSelection({ kind: "panels", keys: out.copies });
  };

  const selectAllOnFace = () => {
    const faceId = faceForSelectAll(draft.panels, selection?.kind === "panels" ? selection.keys : [], selection?.kind === "face" ? selection.id : null, topApi.current?.pointer() ?? null, roof);
    const keys = draft.panels.filter((p) => !faceId || p.face_id === faceId).map((p) => p.key);
    setSelection(keys.length ? { kind: "panels", keys } : null);
    setTab("panels");
  };

  const toggleLayers = () => {
    if (hiddenLayers) {
      setLayers(hiddenLayers);
      setHiddenLayers(null);
    } else {
      setHiddenLayers(layers);
      setLayers(NO_LAYERS);
    }
  };

  const openPlanTab = (t: PlanTab) => {
    if (view === "present") setView("2d");
    setTab(t);
  };

  /** Esc: the drawing in progress, then the comment being written, then the tool, then the selection. */
  const escape = () => {
    if (sheetOpen) return setSheetOpen(false);
    if (layersOpen) return setLayersOpen(false);
    if (view === "present") return setView("2d");
    if (pendingComment) return setPendingComment(null);
    if (topApi.current?.cancel()) return;
    if (tool !== "select") return setTool("select");
    if (selection) return setSelection(null);
    if (activeComment) return openComment(null);
  };

  // ------------------------------------------------------------------------------ keyboard
  const run = (id: string, e: KeyboardEvent) => {
    switch (id) {
      case "tool.select": return setTool("select");
      case "tool.add": return setTool("add");
      case "tool.polygon": return setTool("polygon");
      case "tool.circle": return setTool("circle");
      case "tool.measure": return setTool("measure");
      case "tool.pan": return setTool("pan");
      case "tool.roof": return setTool("roof");
      case "tool.comment":
        if (view !== "2d") setView("2d");
        return setTool("comment");
      case "edit.undo": return !readOnly && dispatch({ type: "undo" });
      case "edit.redo": return !readOnly && dispatch({ type: "redo" });
      case "edit.save":
        if (!readOnly && dirty) saveOnce();
        else if (!readOnly && !dirty) toast(t("editor.save.nothing"));
        return;
      case "edit.delete": return !readOnly && deleteSelected();
      case "edit.duplicate": return duplicate();
      case "edit.rotate": return !readOnly && rotateSelected(5);
      case "edit.rotateBack": return !readOnly && rotateSelected(-5);
      case "edit.rotate90": return !readOnly && rotateSelected(90);
      case "edit.orientation":
        if (readOnly) return;
        if (selected.length) return flipSelected();
        setOrientation((o) => flip(o));
        return toast(t("editor.panels.newOrientation", { orientation: flip(orientation) }));
      case "edit.snap":
        setPlacement((p) => (p === "sticky" ? "free" : "sticky"));
        return toast(placement === "sticky" ? t("editor.snap.offToast") : t("editor.snap.onToast"));
      case "draw.finish": return tool === "polygon" && finishRef.current?.();
      case "nudge.left": return nudge(-NUDGE_M, 0);
      case "nudge.right": return nudge(NUDGE_M, 0);
      case "nudge.up": return nudge(0, NUDGE_M);
      case "nudge.down": return nudge(0, -NUDGE_M);
      case "nudge.leftBig": return nudge(-NUDGE_BIG_M, 0);
      case "nudge.rightBig": return nudge(NUDGE_BIG_M, 0);
      case "nudge.upBig": return nudge(0, NUDGE_BIG_M);
      case "nudge.downBig": return nudge(0, -NUDGE_BIG_M);
      case "select.all": return selectAllOnFace();
      case "select.cancel": return escape();
      case "view.2d": return setView("2d");
      case "view.3d": return setView("3d");
      case "view.present": return setView("present");
      case "view.frame": return frame();
      case "view.layers": return toggleLayers();
      case "view.panHold":
        if (!e.repeat) setPanHeld(true);
        return;
      case "help.sheet": return setSheetOpen((o) => !o);
      case "panel.savings": return openPlanTab("savings");
      case "panel.options": return openPlanTab("options");
      case "panel.setbacks": return openPlanTab("setbacks");
      case "panel.bom": return openPlanTab("bom");
      case "panel.permits": return openPlanTab("permits");
      case "mode.design":
      case "mode.sketch":
      case "mode.permit": {
        const to = workspaceForAction(id, "design");
        return to && workspace.go(to);
      }
      case "tool.section":
        if (view !== "2d") setView("2d");
        return setTool(tool === "section" ? "select" : "section");
      case "layer.report": return tab === "shade" ? setTab("panels") : openPlanTab("shade");
      default:
        analysis.run(id, view === "2d" ? "2d" : "3d");
    }
  };
  const runRef = useRef(run);
  runRef.current = run;
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const typing = isTypingTarget(e.target as HTMLElement | null);
      const action = matchAction(e, bindingsRef.current, { scope: viewRef.current === "2d" ? "2d" : "3d", mac, typing, repeat: e.repeat });
      if (!action) return;
      e.preventDefault();
      runRef.current(action.id, e);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key === " ") setPanHeld(false);
      if (e.key.startsWith("Arrow")) dispatch({ type: "checkpoint" }); // one undo step per held nudge
    };
    const onBlur = () => setPanHeld(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [mac]);

  const tip = (label: MessageKey, id: string) => ({ label: t(label), shortcut: keyOf(id) });
  const hint = selected.length && !readOnly && tool === "select"
    ? t("editor.hint.selection", { count: selected.length, mod: mac ? "⌘" : "Ctrl+" })
    : t(TOOL_HINTS[readOnly && tool === "select" ? "select" : tool], { mod: mac ? "⌘" : "Ctrl" });

  return (
    <div className={`editor-shell${presenting ? " presenting" : ""}`}>
      {!presenting && (
        <header className="editor-top">
          <Link to={`/designs/${designId}`} className="editor-back" aria-label={t("editor.top.back")}>
            <DirIcon><ArrowLeft size={18} /></DirIcon>
          </Link>
          <div className="editor-title">
            <b className="ellipsis" dir="auto">{doc.address || doc.design_name}</b>
            <span className="muted small">
              {t("editor.top.version", { version: shownVersion(doc.version_number, doc.version_offset) })}
              {historical ? ` · ${t("editor.top.readOnly", { current: shownVersion(doc.current_version_number, doc.version_offset) })}` : !canEdit ? ` · ${t("editor.top.viewOnly")}` : ""}
              {dirty ? ` · ${t("editor.top.unsaved")}` : ""}
            </span>
          </div>
          <div className="segmented" role="tablist" aria-label={t("editor.top.views")}>
            <ViewButton on={view === "2d"} onClick={() => setView("2d")} icon={<MapIcon size={15} />} label={t("editor.view.twoD")} shortcut={keyOf("view.2d")} mac={mac} />
            <ViewButton on={view === "3d"} onClick={() => setView("3d")} icon={<Box size={15} />} label={t("editor.view.threeD")} shortcut={keyOf("view.3d")} mac={mac} />
            <ViewButton on={false} onClick={() => setView("present")} icon={<Presentation size={15} />} label={t("editor.view.present")} shortcut={keyOf("view.present")} mac={mac} />
          </div>
          <WorkspaceSwitch current="design" onSwitch={workspace.go} shortcuts={workspaceKeys} />
          <div className="editor-top-actions">
            <button className="icon-btn" onClick={() => setSheetOpen(true)} title={t("editor.top.shortcutsTitle", { key: keyOf("help.sheet") ?? "?" })} aria-label={t("editor.top.shortcuts")} aria-keyshortcuts="?">
              <Keyboard size={18} />
            </button>
            <Button icon={<DirIcon><Undo2 size={16} /></DirIcon>} disabled={!history.past.length || readOnly} onClick={() => dispatch({ type: "undo" })} title={shortcutTitle(t, t("editor.hotkeys.action.edit.undo"), keyOf("edit.undo"), mac)} aria-label={t("editor.hotkeys.action.edit.undo")}>
              <span className="hide-sm">{t("editor.hotkeys.action.edit.undo")}</span>
            </Button>
            <Button icon={<DirIcon><Redo2 size={16} /></DirIcon>} disabled={!history.future?.length || readOnly} onClick={() => dispatch({ type: "redo" })} title={shortcutTitle(t, t("editor.hotkeys.action.edit.redo"), keyOf("edit.redo"), mac)} aria-label={t("editor.hotkeys.action.edit.redo")} />
            {historical ? (
              <Button variant="primary" onClick={() => onOpenVersion(null)}>
                {t("editor.top.openCurrent")}
              </Button>
            ) : !canEdit ? null : (
              <>
                <TextInput placeholder={t("editor.top.notePlaceholder")} value={note} onChange={(e) => setNote(e.target.value)} className="input editor-note" />
                <Button variant="primary" icon={<Save size={16} />} disabled={!dirty} loading={save.isPending} onClick={() => saveOnce()} title={shortcutTitle(t, t("editor.hotkeys.action.edit.save"), keyOf("edit.save"), mac)}>
                  {t("editor.action.save")}
                </Button>
              </>
            )}
          </div>
        </header>
      )}

      <div className="editor-main">
        <div className="editor-canvas">
          {view === "2d" || (presenting && presentView === "2d") ? (
            <TopView
              doc={planDoc}
              roof={roof}
              draft={draft}
              module={module}
              imageUrl={imageUrl}
              tool={presenting ? "pan" : tool}
              readOnly={readOnly || presenting}
              selection={selection}
              conflicts={presenting ? NO_CONFLICTS : conflicts}
              placement={placement}
              orientation={orientation}
              obstacleKind={obstacleKind}
              layers={presenting ? PRESENT_LAYERS : layers}
              panHeld={panHeld}
              mac={mac}
              comments={threads}
              activeComment={activeComment}
              showResolved={showResolved}
              pendingComment={pendingComment}
              apiRef={topApi}
              onSelect={(s) => {
                setSelection(s);
                if (s?.kind === "obstacle") setTab("obstacles");
                if (s?.kind === "face") setTab("roof");
                if (s?.kind === "panels" && tab !== "panels") setTab("panels");
              }}
              onPanels={setPanels}
              onObstacles={(obstacles) => dispatch({ type: "obstacles", obstacles })}
              onRoof={(r, transient) => dispatch({ type: "roof", roof: r, transient })}
              onCheckpoint={() => dispatch({ type: "checkpoint" })}
              onDrawing={setDrawing}
              onCommentBox={(box) => {
                setPendingComment(box);
                setActiveComment(null);
                setTab("comments");
              }}
              onOpenComment={openComment}
              finishRef={finishRef}
              underlay={analysis.underlay}
              overlay={analysis.overlay}
              onHover={analysis.onHover}
              onSection={analysis.onSection}
            />
          ) : maps.loading ? (
            <Spinner label={t("editor.view.loading3d")} />
          ) : (
            <Suspense fallback={<Spinner label={t("editor.view.loading3d")} />}>
              <ThreeView
                doc={planDoc}
                roof={roof}
                draft={draft}
                module={module}
                imageUrl={imageUrl}
                apiKey={maps.key}
                conflicts={presenting ? NO_CONFLICTS : conflicts}
                selectedPanels={selectedKeys}
                presentation={presenting}
                hideDesign={!!hiddenLayers}
                apiRef={threeApi}
                onScene={analysis.onScene}
                onPick={(pick) => {
                  if (presenting) return;
                  if (!pick) return setSelection(null);
                  if (pick.kind === "obstacle") {
                    setSelection({ kind: "obstacle", key: pick.key });
                    setTab("obstacles");
                    return;
                  }
                  const keys = pick.additive && selection?.kind === "panels" ? selection.keys : [];
                  setSelection({ kind: "panels", keys: keys.includes(pick.key) ? keys.filter((k) => k !== pick.key) : [...keys, pick.key] });
                  setTab("panels");
                }}
              />
              <ThreeAnalysis scene={analysis.scene} store={analysis.store} data={analysis.data} roof={roof} draft={draft} module={module} yields={analysis.yields} />
            </Suspense>
          )}
          {view === "2d" && (
            <div className="editor-tools" role="toolbar" aria-label={t("editor.tools.label")}>
              <ToolButton icon={<MousePointer2 />} {...tip("editor.hotkeys.action.tool.select", "tool.select")} mac={mac} active={tool === "select"} onClick={() => setTool("select")} />
              {!readOnly && (
                <>
                  <ToolButton icon={<Plus />} {...tip("editor.hotkeys.action.tool.add", "tool.add")} mac={mac} active={tool === "add"} onClick={() => setTool("add")} />
                  <ToolButton icon={<Pentagon />} {...tip("editor.hotkeys.action.tool.polygon", "tool.polygon")} mac={mac} active={tool === "polygon"} onClick={() => setTool("polygon")} />
                  <ToolButton icon={<Circle />} {...tip("editor.hotkeys.action.tool.circle", "tool.circle")} mac={mac} active={tool === "circle"} onClick={() => setTool("circle")} />
                  <ToolButton icon={<Spline />} {...tip("editor.hotkeys.action.tool.roof", "tool.roof")} mac={mac} active={tool === "roof"} onClick={() => setTool("roof")} />
                </>
              )}
              <ToolButton icon={<Ruler />} {...tip("editor.hotkeys.action.tool.measure", "tool.measure")} mac={mac} active={tool === "measure"} onClick={() => setTool("measure")} />
              <ToolButton icon={<Hand />} {...tip("editor.tools.pan", "tool.pan")} mac={mac} active={tool === "pan" || panHeld} onClick={() => setTool("pan")} />
              <ToolButton icon={<MessageSquare />} {...tip("editor.hotkeys.action.tool.comment", "tool.comment")} mac={mac} active={tool === "comment"} onClick={() => setTool("comment")} />
              <span className="sep" />
              {!readOnly && (
                <ToolButton
                  icon={placement === "sticky" ? <Magnet /> : <Hand />}
                  label={placement === "sticky" ? t("editor.snap.onTip") : t("editor.snap.offTip")}
                  shortcut={keyOf("edit.snap")}
                  mac={mac}
                  active={placement === "sticky"}
                  onClick={() => setPlacement(placement === "sticky" ? "free" : "sticky")}
                />
              )}
              <ToolButton icon={<LayersIcon />} label={hiddenLayers ? t("editor.tools.layersHidden") : t("editor.tools.layers")} shortcut={keyOf("view.layers")} mac={mac} active={layersOpen || !!hiddenLayers} onClick={() => setLayersOpen((o) => !o)} />
              {tool === "polygon" && drawing >= 3 && (
                <Button size="sm" variant="primary" onClick={() => finishRef.current?.()}>
                  {t("editor.tools.finishObstacle")}
                </Button>
              )}
            </div>
          )}
          {!presenting && (
            <AnalysisDock
              store={analysis.store}
              data={analysis.data}
              loading={analysis.loading}
              error={analysis.error}
              view={view === "2d" ? "2d" : "3d"}
              roof={roof}
              yields={analysis.yields}
              mac={mac}
              keyOf={keyOf}
              sectionTool={tool === "section"}
              onSectionTool={() => setTool(tool === "section" ? "select" : "section")}
              apiRef={analysis.dockRef}
            />
          )}
          {view === "2d" && layersOpen && (
            <LayersMenu
              extra={<AnalysisMenuRows store={analysis.store} keyOf={keyOf} mac={mac} />}
              layers={layers}
              onChange={(l) => {
                setLayers(l);
                setHiddenLayers(null);
              }}
              onClose={() => setLayersOpen(false)}
              shortcut={keyOf("view.layers")}
              mac={mac}
            />
          )}
          {view === "2d" && (
            <div className="editor-hint" role="status">
              {panHeld ? t("editor.hint.panning") : hint}
              <button className="hint-keys" onClick={() => setSheetOpen(true)}>
                <FormattedMessage id="editor.hint.allShortcuts" values={{ keys: <Keys combo={keyOf("help.sheet") ?? "?"} mac={mac} /> }} />
              </button>
            </div>
          )}
          {presenting && (
            <PresentationOverlay
              title={doc.address || doc.design_name || t("editor.presentation.defaultTitle")}
              numbers={numbers}
              pricing={pricing}
              savings={liveSavings.result}
              savingsLoading={liveSavings.loading}
              payment={payment}
              onPayment={setPayment}
              faces={faceToggles}
              onFace={toggleFace}
              view={presentView}
              onView={setPresentView}
              onExit={() => setView("2d")}
            />
          )}
          <NumbersBar numbers={numbers} pricing={pricing} currency={currency} conflicts={conflictCount} />
          {carry && !carryClosed && !presenting && (
            <div className={`editor-carry is-${carry.tone}`} role="status">
              <Sparkles size={16} aria-hidden />
              <div>
                <b>{t(carry.title.key, carry.title.values)}</b>
                <span>
                  {t(carry.detail.key, carry.detail.values)}
                  {carry.missing ? ` ${t(carry.missing.key, carry.missing.values)}` : ""}
                </span>
              </div>
              <button type="button" className="editor-carry-close" aria-label={t("editor.carry.dismiss")} onClick={() => setCarryClosed(true)}><X size={14} /></button>
            </div>
          )}
          {perf && <PerfOverlay />}
        </div>

        {!presenting && (
          <aside className="editor-side">
            <div className="side-tabbars">
              <SideTabs
                label={t("editor.side.designTabs")}
                active={tab}
                onSelect={setTab}
                tabs={(Object.keys(DESIGN_TABS) as (keyof typeof DESIGN_TABS)[]).map((id): SideTabDef<SideTab> => {
                  const open = id === "comments" ? threads.filter((x) => x.status === "open").length : 0;
                  return {
                    id,
                    label: t(DESIGN_TABS[id]),
                    icon: id === "versions" ? <History size={14} /> : id === "comments" ? <MessageSquare size={14} /> : undefined,
                    iconOnly: id === "versions",
                    name: id === "versions" ? t("editor.side.tab.versionsName") : undefined,
                    badge: open > 0 ? { text: open, title: t("editor.side.openThreads", { count: open }) } : null,
                  };
                })}
              />
              <SideTabs
                label={t("editor.side.planTabs")}
                className="plan-tabs"
                active={tab}
                onSelect={setTab}
                tabs={PLAN_TABS.map(([id, label, name, action]): SideTabDef<SideTab> => {
                  const combo = keyOf(action);
                  return {
                    id,
                    label: t(label),
                    name: t(name),
                    shortcut: combo ? formatCombo(combo, mac) : null,
                    badge: id === "setbacks" && setbacks.rulesChanged && !historical ? { text: "!", title: t("editor.setbacks.rulesChanged.title"), label: t("editor.side.badge.rulesChanged") } : null,
                  };
                })}
              />
            </div>
            <div className="side-body">
              {tab === "panels" && (
                <section className="stack">
                  <div className="row wrap">
                    <span className="muted small">{t("editor.panels.newPanels")}</span>
                    <div className="segmented">
                      {(["portrait", "landscape"] as Orientation[]).map((o) => (
                        <button key={o} className={orientation === o ? "on" : ""} onClick={() => setOrientation(o)} title={shortcutTitle(t, t("editor.hotkeys.action.edit.orientation"), keyOf("edit.orientation"), mac)}>
                          {o === "portrait" ? t("editor.orientation.portrait") : t("editor.orientation.landscape")}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="muted small">
                    {t("editor.panels.module", { model: doc.module!.model, watts: num(doc.module!.watts), width: num(module.width_m, 2), height: num(module.height_m, 2) })}{" "}
                    {placement === "sticky" ? t("editor.snap.onNote") : t("editor.snap.offNote")}
                  </p>
                  {selected.length > 0 ? (
                    <div className="side-card">
                      <b>{t("editor.panels.selected", { count: selected.length })}</b>
                      <div className="row wrap">
                        <Button size="sm" icon={<RotateCcw size={14} />} onClick={() => rotateSelected(-5)} disabled={readOnly} title={shortcutTitle(t, t("editor.hotkeys.action.edit.rotateBack"), keyOf("edit.rotateBack"), mac)}>
                          <span dir="ltr">−5°</span>
                        </Button>
                        <Button size="sm" icon={<RotateCw size={14} />} onClick={() => rotateSelected(5)} disabled={readOnly} title={shortcutTitle(t, t("editor.hotkeys.action.edit.rotate"), keyOf("edit.rotate"), mac)}>
                          <span dir="ltr">+5°</span>
                        </Button>
                        <Button size="sm" onClick={flipSelected} disabled={readOnly} title={shortcutTitle(t, t("editor.hotkeys.action.edit.orientation"), keyOf("edit.orientation"), mac)}>
                          {t("editor.hotkeys.action.edit.orientation")}
                        </Button>
                        <Button size="sm" onClick={duplicate} disabled={readOnly} title={shortcutTitle(t, t("editor.panels.duplicate"), keyOf("edit.duplicate"), mac)}>
                          {t("editor.panels.duplicate")}
                        </Button>
                        <Button size="sm" variant="danger" icon={<Trash2 size={14} />} onClick={deleteSelected} disabled={readOnly} title={shortcutTitle(t, t("editor.action.delete"), keyOf("edit.delete"), mac)}>
                          {t("editor.action.delete")}
                        </Button>
                      </div>
                      {selected.map((p) =>
                        conflicts[p.key]?.length ? (
                          <div key={p.key} className="small warn-text">
                            {intl.formatList(conflicts[p.key]!.map((k) => t(CONFLICT_LABEL_KEYS[k])))}
                          </div>
                        ) : null,
                      )}
                    </div>
                  ) : (
                    <p className="muted small">{t("editor.panels.help", { mod: mac ? "⌘" : "Ctrl" })}</p>
                  )}
                  <div className="side-card">
                    <b>{t("editor.panels.faces")}</b>
                    {roof.faces.map((f) => {
                      const count = draft.panels.filter((p) => p.face_id === f.id).length;
                      return (
                        <div key={f.id} className="face-row">
                          <span>
                            {t("editor.panels.face", { label: f.label })} <span className="muted small">· {t("editor.panels.count", { count })}</span>
                          </span>
                          <span className="row">
                            <Button size="sm" disabled={readOnly} onClick={() => fillSelectedFace(f.id)}>
                              {t("editor.panels.fill")}
                            </Button>
                            <Button size="sm" variant="ghost" disabled={readOnly || !count} onClick={() => clearFace(f.id)}>
                              {t("editor.panels.clear")}
                            </Button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {conflictCount > 0 && (
                    <Alert tone="warn" title={t("editor.panels.conflictsTitle", { count: conflictCount })}>
                      {t("editor.panels.conflictsBody")}
                      {serverConflicts.length > 0 && <div className="small">{t("editor.panels.serverConflicts", { count: serverConflicts.length })}</div>}
                    </Alert>
                  )}
                </section>
              )}

              {tab === "obstacles" && (
                <section className="stack">
                  <div className="row wrap">
                    <span className="muted small">{t("editor.obstacles.drawAs")}</span>
                    <Select value={obstacleKind} onChange={setObstacleKind} options={OBSTACLE_KINDS.map((k) => ({ value: k, label: t("editor.obstacles.kind", { kind: k }) }))} />
                  </div>
                  <p className="muted small">{t("editor.obstacles.help")}</p>
                  <div className="obstacle-list">
                    {draft.obstacles.length === 0 && <p className="muted small">{t("editor.obstacles.none")}</p>}
                    {draft.obstacles.map((o, i) => (
                      <div key={o.key} className={`face-row${selection?.kind === "obstacle" && selection.key === o.key ? " selected" : ""}`} onClick={() => setSelection({ kind: "obstacle", key: o.key })}>
                        <span>
                          {t("editor.obstacles.kind", { kind: o.kind })} {o.id ?? t("editor.obstacles.newName", { number: i + 1 })}{" "}
                          <Badge tone={o.source === "manual" ? "grad" : "info"} plain>
                            {o.source === "manual" ? t("editor.obstacles.drawn") : o.confidence != null ? intl.formatNumber(o.confidence, { style: "percent" }) : t("editor.obstacles.detected")}
                          </Badge>
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Trash2 size={14} />}
                          disabled={readOnly}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch({ type: "obstacles", obstacles: draft.obstacles.filter((x) => x.key !== o.key) });
                          }}
                          aria-label={t("editor.obstacles.delete")}
                        />
                      </div>
                    ))}
                  </div>
                  {doc.rejected_obstructions.length > 0 && <p className="muted small">{t("editor.obstacles.rejected", { count: doc.rejected_obstructions.length })}</p>}
                </section>
              )}

              {tab === "roof" && (
                <RoofPanel roof={roof} draft={draft} readOnly={readOnly} selectedFace={faceForSelection?.id ?? null} onSelectFace={(id) => setSelection({ kind: "face", id })} onRoof={(r) => dispatch({ type: "roof", roof: r })} />
              )}

              {tab === "shade" && (
                <ShadeReportPanel
                  designId={designId}
                  version={doc.version_number}
                  shownVersion={shownVersion(doc.version_number, doc.version_offset)}
                  data={analysis.data}
                  loading={analysis.loading}
                  roof={roof}
                  yields={analysis.yields}
                  dirty={dirty}
                  selected={selectedKeys}
                  onSelectPanel={(key) => setSelection({ kind: "panels", keys: [key] })}
                />
              )}

              {tab === "comments" && (
                <CommentsPanel
                  designId={designId}
                  version={doc.version_number}
                  versionOffset={doc.version_offset}
                  threads={threads}
                  loading={comments.isLoading}
                  active={activeComment}
                  onActive={(id) => {
                    openComment(id);
                    const t = threads.find((x) => x.id === id);
                    if (t && view === "2d") topApi.current?.frame([t.anchor.min_xy as Vec2, t.anchor.max_xy as Vec2]);
                  }}
                  pendingBox={pendingComment}
                  onCancelPending={() => {
                    setPendingComment(null);
                    if (tool === "comment") setTool("select");
                  }}
                  showResolved={showResolved}
                  onShowResolved={setShowResolved}
                />
              )}

              {tab === "savings" && (
                <SavingsPanel
                  designId={designId}
                  inputs={savingsInputs.inputs}
                  setInputs={savingsInputs.setInputs}
                  saveState={savingsInputs.state}
                  saveError={savingsInputs.error}
                  canEdit={canEdit && !historical}
                  result={liveSavings.result}
                  loading={liveSavings.loading}
                  error={liveSavings.error}
                  selected={payment}
                  onSelect={setPayment}
                />
              )}
              {tab === "options" && <OptionsPanel designId={designId} canEdit={canEdit} dirty={isDirty(draft)} />}
              {tab === "setbacks" && <SetbacksPanel designId={designId} canEdit={canEdit && !historical} setbacks={setbacks} historical={historical} apply={{ run: () => applySetbacks.mutate(), pending: applySetbacks.isPending, blocked: isDirty(draft) }} />}
              {tab === "permits" && (
                <section className="stack plan-panel">
                  <div className="stack bom-head">
                    <h3 className="bom-title">{t("editor.permits.title")}</h3>
                    <span className="small muted">{t("editor.permits.body")}</span>
                  </div>
                  <PermitSummary designId={designId} version={doc.current_version_number} offset={doc.version_offset} shortcut={keyOf("mode.permit") ? formatCombo(keyOf("mode.permit")!, mac) : null} dirty={dirty} onOpen={(href) => goPermit(href)} />
                </section>
              )}
              {tab === "bom" && <BomPanel designId={designId} version={doc.version_number} shownVersion={shownVersion(doc.version_number, doc.version_offset)} panels={draft.panels} address={doc.address} designName={doc.design_name} />}

              {tab === "versions" && <VersionsPanel designId={designId} offset={doc.version_offset} current={doc.current_version_number} open={doc.version_number} dirty={dirty} canEdit={canEdit} onOpen={onOpenVersion} onRestored={onSaved} />}
            </div>
          </aside>
        )}
      </div>
      {leaveTo && (
        <Modal
          title={t("editor.workspace.ask.permit.title")}
          onClose={() => setLeaveTo(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setLeaveTo(null)}>{t("editor.leave.stay")}</Button>
              <Button onClick={() => navigate(leaveTo)}>{t("editor.leave.openWithoutSaving")}</Button>
              <Button variant="primary" loading={save.isPending} onClick={() => saveOnce(() => navigate(leaveTo))}>{t("editor.leave.saveAndOpen")}</Button>
            </>
          }
        >
          <p style={{ margin: 0 }}>{t("editor.leave.body", { version: shownVersion(doc.current_version_number, doc.version_offset) })}</p>
        </Modal>
      )}
      {backAsked && (
        <Modal
          title={t("editor.leave.backTitle")}
          onClose={() => setBackAsked(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setBackAsked(false)}>{t("editor.leave.stay")}</Button>
              <Button onClick={backGuard.leave}>{t("editor.leave.leaveWithoutSaving")}</Button>
              <Button variant="primary" loading={save.isPending} onClick={() => saveOnce(backGuard.leave)}>{t("editor.leave.saveAndLeave")}</Button>
            </>
          }
        >
          <p style={{ margin: 0 }}>{t("editor.leave.backBody")}</p>
        </Modal>
      )}
      {workspace.dialog}
      {sheetOpen && <CheatSheet mac={mac} overrides={overrides} onOverrides={setOverrides} onClose={() => setSheetOpen(false)} />}
    </div>
  );
}

function shortcutTitle(t: Translate, label: string, combo: string | null, mac: boolean): string {
  if (!combo) return label;
  const parts = combo.split("+");
  const key = parts.pop()!;
  const names: Record<string, string> = mac ? { mod: "⌘", shift: "⇧", alt: "⌥", ctrl: "⌃" } : { mod: "Ctrl+", shift: "Shift+", alt: "Alt+", ctrl: "Ctrl+" };
  const keyName: Record<string, string> = { arrowleft: "←", arrowright: "→", arrowup: "↑", arrowdown: "↓", escape: "Esc", delete: "Del", backspace: "⌫", space: "Space", enter: "Enter" };
  return t("editor.shortcut.title", { label, keys: `${parts.map((m) => names[m] ?? m).join("")}${keyName[key] ?? key.toUpperCase()}` });
}

function ViewButton({ on, onClick, icon, label, shortcut, mac }: { on: boolean; onClick: () => void; icon: ReactNode; label: string; shortcut: string | null; mac: boolean }) {
  const t = useT();
  return (
    <button className={on ? "on" : ""} onClick={onClick} title={shortcutTitle(t, label, shortcut, mac)} aria-label={label} aria-keyshortcuts={shortcut ?? undefined}>
      {icon} <span className="ed-view-label">{label}</span>
    </button>
  );
}

/** A tool button whose tooltip names the tool and its key (Figma, Blender): shown on hover and on focus. */
function ToolButton({ icon, label, shortcut, mac, active, onClick }: { icon: ReactNode; label: string; shortcut?: string | null; mac: boolean; active?: boolean; onClick: () => void }) {
  return (
    <button className={`tool${active ? " on" : ""}`} onClick={onClick} aria-label={label} aria-pressed={!!active} aria-keyshortcuts={shortcut ?? undefined}>
      {icon}
      <span className="tool-tip" role="tooltip">
        {label}
        {shortcut && <Keys combo={shortcut} mac={mac} />}
      </span>
    </button>
  );
}

function LayersMenu({ layers, onChange, onClose, shortcut, mac, extra }: { layers: Layers; onChange: (l: Layers) => void; onClose: () => void; shortcut: string | null; mac: boolean; extra?: ReactNode }) {
  const t = useT();
  const rows: [keyof Layers, MessageKey][] = [
    ["imagery", "editor.layers.imagery"],
    ["panels", "editor.layers.panels"],
    ["obstacles", "editor.layers.obstacles"],
    ["setbacks", "editor.layers.setbacks"],
    ["comments", "editor.layers.comments"],
  ];
  return (
    <div className="layers-menu" role="dialog" aria-label={t("editor.layers.title")}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <b>{t("editor.layers.title")}</b>
        <button className="icon-btn" onClick={onClose} aria-label={t("editor.action.close")}>
          <X size={14} />
        </button>
      </div>
      {rows.map(([k, label]) => (
        <label key={k} className="row small">
          <input type="checkbox" checked={layers[k]} onChange={(e) => onChange({ ...layers, [k]: e.target.checked })} /> {t(label)}
        </label>
      ))}
      {shortcut && (
        <div className="muted small">
          <FormattedMessage id="editor.layers.toggleAll" values={{ keys: <Keys combo={shortcut} mac={mac} /> }} />
        </div>
      )}
      {extra}
    </div>
  );
}

function NumbersBar({ numbers, pricing, currency, conflicts }: { numbers: S["EditorNumbers"]; pricing: boolean; currency: string; conflicts: number }) {
  const t = useT();
  return (
    <div className={`numbers-bar${pricing ? " pricing" : ""}`} aria-live="polite">
      <Stat label={t("editor.numbers.panels")} value={num(numbers.panel_count)} />
      <Stat label={t("editor.numbers.system")} value={`${num(numbers.kw_dc, 2)} kW`} />
      <Stat label={t("editor.numbers.production")} value={t("editor.numbers.kwhPerYear", { value: num(numbers.annual_kwh) })} />
      <Stat label={t("editor.numbers.price")} value={money(numbers.price, currency)} />
      <Stat label={t("editor.numbers.savings")} value={numbers.annual_savings != null ? t("editor.numbers.perYear", { amount: money(numbers.annual_savings, currency) }) : "—"} />
      <Stat label={t("editor.numbers.payback")} value={numbers.payback_years != null ? t("editor.numbers.years", { years: Math.round(numbers.payback_years * 10) / 10 }) : "—"} />
      {conflicts > 0 && <span className="numbers-warn">{t("editor.numbers.conflicts", { count: conflicts })}</span>}
      {pricing && <span className="spinner" style={{ width: 14, height: 14 }} aria-label={t("editor.numbers.updating")} />}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="nb-stat">
      <span>{label}</span>
      <b dir="auto">{value}</b>
    </div>
  );
}

function RoofPanel({
  roof,
  draft,
  readOnly,
  selectedFace,
  onSelectFace,
  onRoof,
}: {
  roof: ReturnType<typeof liveRoof>;
  draft: Draft;
  readOnly: boolean;
  selectedFace: string | null;
  onSelectFace: (id: string) => void;
  onRoof: (r: Draft["roof"]) => void;
}) {
  const t = useT();
  const setFace = (id: string, patch: { pitch_deg?: number; azimuth_deg?: number }) =>
    onRoof({ ...draft.roof, faces: { ...draft.roof.faces, [id]: { ...draft.roof.faces[id], ...patch } } });
  const edgeTotals = roof.edges.reduce<Record<string, number>>((acc, e) => ({ ...acc, [e.type]: (acc[e.type] ?? 0) + e.length }), {});
  return (
    <section className="stack">
      <div className="side-card">
        <b>{t("editor.roof.summary")}</b>
        <dl className="kv">
          <dt>{t("editor.roof.faces")}</dt>
          <dd>{roof.faces.length}</dd>
          <dt>{t("editor.roof.area")}</dt>
          <dd>{num(roof.area, 1)} m²</dd>
          <dt>{t("editor.roof.footprint")}</dt>
          <dd>{num(roof.groundArea, 1)} m²</dd>
          <dt>{t("editor.roof.mainPitch")}</dt>
          <dd>{roof.predominantPitch != null ? `${num(roof.predominantPitch, 1)}°` : "—"}</dd>
          {Object.entries(edgeTotals).map(([type, length]) => (
            <FragmentKV key={type} k={t("editor.roof.edgeType", { type })} v={`${num(length, 1)} m`} />
          ))}
        </dl>
      </div>
      <p className="muted small">{t("editor.roof.help")}</p>
      <table className="face-table">
        <thead>
          <tr>
            <th>{t("editor.roof.colFace")}</th>
            <th>{t("editor.roof.colPitch")}</th>
            <th>{t("editor.roof.colDirection")}</th>
            <th>{t("editor.roof.colArea")}</th>
          </tr>
        </thead>
        <tbody>
          {roof.faces.map((f) => (
            <tr key={f.id} className={selectedFace === f.id ? "selected" : ""} onClick={() => onSelectFace(f.id)}>
              <td>
                {f.label}
                {f.edited ? " ✎" : ""}
              </td>
              <td>
                <NumberInput value={Math.round(f.pitch * 10) / 10} min={0} max={75} step={0.5} suffix="°" disabled={readOnly} onChange={(v) => v != null && setFace(f.id, { pitch_deg: v })} />
              </td>
              <td>
                <NumberInput value={Math.round(f.azimuth)} min={0} max={359} step={1} suffix="°" disabled={readOnly} onChange={(v) => v != null && setFace(f.id, { azimuth_deg: ((v % 360) + 360) % 360 })} />
              </td>
              <td className="num">{num(f.area, 1)} m²</td>
            </tr>
          ))}
        </tbody>
      </table>
      {Object.keys(draft.roof.vertices).length > 0 && <p className="small muted">{t("editor.roof.cornersMoved", { count: Object.keys(draft.roof.vertices).length })}</p>}
    </section>
  );
}

function FragmentKV({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

function VersionsPanel({
  designId,
  offset,
  current,
  open,
  dirty,
  canEdit,
  onOpen,
  onRestored,
}: {
  designId: string;
  /** `version_offset` (see shownVersion) */
  offset?: number | null;
  current: number;
  open: number;
  dirty: boolean;
  canEdit: boolean;
  onOpen: (n: number | null) => void;
  onRestored: (n: number) => void;
}) {
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const stages = (names: string[]) => intl.formatList(names.map((stage) => t("editor.versions.stage", { stage })));
  const versions = useQuery({
    queryKey: ["versions", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/versions", { params: { path: { design_id: designId }, query: { limit: 50 } } })),
  });
  const restore = useMutation({
    mutationFn: (n: number) =>
      unwrap(api.POST("/v1/designs/{design_id}/versions/{version_number}/restore", { params: { path: { design_id: designId, version_number: n } }, body: { note: t("editor.versions.restoreNote", { version: shownVersion(n, offset) }) } })),
    onSuccess: (v) => onRestored(v.number),
    onError: (e) => toast(errorMessage(e), true),
  });
  if (versions.isLoading) return <Spinner label={t("editor.loading")} />;
  return (
    <section className="stack">
      <p className="muted small">{t("editor.versions.help")}</p>
      <ol className="version-list">
        {versions.data?.items.filter((v) => !(offset && v.number === 1 && v.origin === "created")).map((v) => (
          <li key={v.id} className={v.number === open ? "open" : ""}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <b>
                {t("editor.versions.short", { version: shownVersion(v.number, offset) })} {v.is_current && <Badge tone="ok">{t("editor.versions.current")}</Badge>}
              </b>
              <span className="muted small">{date(v.created_at, true)}</span>
            </div>
            <div className="small">
              {originLabel(t, v, offset)}
              {v.created_by ? ` · ${v.created_by}` : ""}
            </div>
            {v.note && <div className="small muted">“{v.note}”</div>}
            {v.edit && (
              <div className="small muted">
                {t("editor.versions.edited", { stages: stages(v.edit.stages) })}
                {v.edit.recomputed.length ? ` · ${t("editor.versions.recomputed", { stages: stages(v.edit.recomputed) })}` : ""}
                {v.edit.failed ? ` · ${t("editor.versions.failed", { stage: t("editor.versions.stage", { stage: v.edit.failed }) })}` : ""}
              </div>
            )}
            {!v.is_current && (
              <div className="row">
                <Button size="sm" onClick={() => onOpen(v.number)} disabled={v.number === open}>
                  {t("editor.versions.view")}
                </Button>
                {canEdit && (
                <Button
                  size="sm"
                  disabled={dirty}
                  loading={restore.isPending && restore.variables === v.number}
                  title={dirty ? t("editor.versions.saveFirst") : undefined}
                  onClick={() => restore.mutate(v.number)}
                >
                  {t("editor.versions.restore")}
                </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
      {current !== open && (
        <Button onClick={() => onOpen(null)} variant="primary">
          {t("editor.versions.backToCurrent")}
        </Button>
      )}
    </section>
  );
}

function originLabel(t: Translate, v: S["DesignVersion"], offset: number | null | undefined): string {
  switch (v.origin) {
    case "manual_edit":
      return t("editor.versions.origin.manualEdit");
    case "restore":
      return t("editor.versions.origin.restore", { version: v.restored_from_number != null ? shownVersion(v.restored_from_number, offset) : "?" });
    case "auto_pipeline":
      return t("editor.versions.origin.autoPipeline");
    case "created":
      return t("editor.versions.origin.created");
    case "duplicate":
      return t("editor.versions.origin.duplicate");
    case "import":
      return t("editor.versions.origin.import");
    case "sketch":
      return t("editor.versions.origin.sketch");
    default:
      return t("editor.versions.origin.other");
  }
}
